import {
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Box, Stack } from "@mui/system";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { useDispatch } from "react-redux";
import isEqual from "lodash/isEqual";
const DuplicateBudgetEdit = () => {
  const selectedBudgetBrands = useSelector(
    (state: any) => state.engagementIncentives?.selectedBudgetBrands || []
  );
  const editedBudgetsFromStore = useSelector(
    (state: any) => state.engagementIncentives?.editedBudgets || []
  );
  const [editedBudgets, setEditedBudgets] = useState<any[]>([]);
  const [promotedOnly, setPromotedOnly] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [linkSelections, setLinkSelections] = useState<{
    [key: string]: any[];
  }>({});

  const dispatch = useDispatch();
  // Initialize edited budgets from selected brands
  useEffect(() => {
    if (editedBudgetsFromStore && editedBudgetsFromStore.length > 0) {
      setEditedBudgets(editedBudgetsFromStore);
      // Initialize linkSelections from editedBudgetsFromStore
      const linkSelInit: { [key: string]: any[] } = {};
      editedBudgetsFromStore.forEach((budget: any) => {
        linkSelInit[budget.brand_shared_uid] = budget.selected_links || [];
      });
      setLinkSelections(linkSelInit);
    } else if (selectedBudgetBrands && selectedBudgetBrands.length > 0) {
      // Map selectedBudgetBrands into editedBudgets form
      const initialBudgets = selectedBudgetBrands?.map((brand: any) => {
        const filteredLinks = (brand.link_details || []).filter(
          (l: any) => l.shown_in_active_table === true
        );
        return {
          cost_per_qftd: brand.cost_per_qftd,
          qftd_budget: brand.qftd_budget,
          brand_shared_uid: brand.brand_shared_uid,
          brand_name: brand.brand_name,
          selected_links:
            filteredLinks.length > 0
              ? filteredLinks.map((l: any) => ({
                  link_shared_uid: l.link_shared_uid,
                  link_name: l.link_name,
                  shown_in_active_table: true,
                }))
              : [{}], // show one empty field if no links are available
        };
      });

      setEditedBudgets(initialBudgets);

      const linkSelInit: { [key: string]: any[] } = {};
      selectedBudgetBrands.forEach((brand: any) => {
        const filteredLinks = (brand.link_details || []).filter(
          (l: any) => l.shown_in_active_table === true
        );

        linkSelInit[brand.brand_shared_uid] =
          filteredLinks.length > 0
            ? filteredLinks.map((l: any) => ({
                link_shared_uid: l.link_shared_uid,
                link_name: l.link_name,
                shown_in_active_table: true,
              }))
            : [{}]; // empty object as placeholder to show a blank Autocomplete
      });
      setLinkSelections(linkSelInit);
    }
    setPromotedOnly((prev) => {
      if (Object.keys(prev).length === 0) {
        const promotedInit: { [key: string]: boolean } = {};
        selectedBudgetBrands.forEach((brand: any) => {
          promotedInit[brand.brand_shared_uid] = true;
        });
        return promotedInit;
      }
      return prev;
    });
  }, [selectedBudgetBrands, editedBudgetsFromStore]);

  // Sync local editedBudgets + linkSelections to Redux store on change
  useEffect(() => {
    if (editedBudgets.length === 0) return; // Do not dispatch empty on init

    const updatedBudgets = editedBudgets?.map((budget) => {
      const links = linkSelections[budget.brand_shared_uid] || [];
      return {
        ...budget,
        selected_links: links?.map((link: any) => ({
          link_shared_uid: link.link_shared_uid,
          link_name: link.link_name,
          shown_in_active_table: link.shown_in_active_table || false,
        })),
      };
    });
    //Install npm i --save-dev @types/lodash
    if (!isEqual(updatedBudgets, editedBudgetsFromStore)) {
      dispatch({
        type: "engagementIncentives/setEditedBudgets",
        payload: updatedBudgets,
      });
    }
  }, [editedBudgets, linkSelections, editedBudgetsFromStore, dispatch]);

  const handleCostChange = (brandId: string, value: string) => {
    const num = Number(value);
    if (value === "" || num > 0) {
      setEditedBudgets((prev) =>
        prev.map((budget) =>
          budget.brand_shared_uid === brandId
            ? { ...budget, cost_per_qftd: value === "" ? "" : num }
            : budget
        )
      );
    }
  };
  const handleTogglePromotedOnly = (brandId: string) => {
    setPromotedOnly((prev) => ({
      ...prev,
      [brandId]: !prev[brandId],
    }));
  };

  // Handle changes to the QFTD budget
  const handleBudgetChange = (brandId: string, value: string) => {
    const num = Number(value);
    if (value === "" || num > 0) {
      setEditedBudgets((prev) =>
        prev.map((budget) =>
          budget.brand_shared_uid === brandId
            ? { ...budget, qftd_budget: value === "" ? "" : num }
            : budget
        )
      );
    }
  };
  const handleLinkChange = (brandId: string, index: number, newLink: any) => {
    setLinkSelections((prev) => {
      const updated = [...(prev[brandId] || [])];
      if (newLink) {
        updated[index] = newLink;
      } else {
        updated.splice(index, 1);
      }
      return { ...prev, [brandId]: updated };
    });
  };

  const handleLinkRemove = (brandId: string, index: number) => {
    setLinkSelections((prev: any) => {
      const updated = [...prev[brandId]];
      updated.splice(index, 1);
      return { ...prev, [brandId]: updated };
    });
  };

  const handleAddLink = (brandId: string, brand: any) => {
    const allLinks = [
      ...(brand.link_details || []),
      ...(brand.link_details_from_brand || []),
    ];

    setLinkSelections((prev: any) => {
      const existingLinks = prev[brandId] || [];
      const availableLinks = allLinks.filter(
        (l) =>
          !existingLinks.find(
            (e: any) => e.link_shared_uid === l.link_shared_uid
          )
      );

      if (availableLinks.length > 0) {
        return {
          ...prev,
          [brandId]: [...existingLinks, availableLinks[0]],
        };
      }

      return prev;
    });
  };
  const getAvailableLinks = (
    brand: any,
    selectedLinks: any[],
    isPromotedOnly: boolean
  ) => {
    const allLinks = [
      ...(brand.link_details || []),
      ...(brand.link_details_from_brand || []),
    ];

    return allLinks.filter((link: any) => {
      const isAlreadySelected = selectedLinks.some(
        (sel: any) => sel.link_shared_uid === link.link_shared_uid
      );
      const matchesPromotionFilter = isPromotedOnly
        ? link.shown_in_active_table === true
        : link.shown_in_active_table === false;

      return !isAlreadySelected && matchesPromotionFilter;
    });
  };

  return (
    <Box sx={{ mt: 3 }}>
      {selectedBudgetBrands?.map((brand: any) => {
        const brandId = brand.brand_shared_uid;
        const brandLinks = linkSelections[brandId] || [];
        const totalAvailableLinks = [
          ...(brand.link_details || []),
          ...(brand.link_details_from_brand || []),
        ];

        const availableLinks = getAvailableLinks(
          brand,
          brandLinks,
          promotedOnly[brandId]
        );
        const currentBudget =
          editedBudgets.find((b) => b.brand_shared_uid === brandId) || brand;
        const allLinks = [
          ...(brand.link_details || []),
          ...(brand.link_details_from_brand || []),
        ];
        return (
          <Box key={brandId} sx={{ mb: 4 }}>
            <Typography>{brand.brand_name}</Typography>
            <Stack
              flexDirection={"row"}
              gap={2}
              sx={{ mb: 2 }}
              justifyContent={"space-between"}
              mt={2}
            >
              <TextField
                id={`cost-${brandId}`}
                label="QFTD Cost"
                color="secondary"
                size="small"
                fullWidth
                value={currentBudget.cost_per_qftd}
                onChange={(e) => handleCostChange(brandId, e.target.value)}
                type="number"
                error={
                  currentBudget.cost_per_qftd !== "" &&
                  Number(currentBudget.cost_per_qftd) <= 0
                }
                helperText={
                  currentBudget.cost_per_qftd !== "" &&
                  Number(currentBudget.cost_per_qftd) <= 0
                    ? "Must be a positive number"
                    : ""
                }
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                sx={{ width: "180px" }}
              />
              <TextField
                id={`budget-${brandId}`}
                label="QFTD Budget"
                color="secondary"
                size="small"
                value={currentBudget.qftd_budget}
                onChange={(e) => handleBudgetChange(brandId, e.target.value)}
                fullWidth
                error={
                  currentBudget.qftd_budget !== "" &&
                  Number(currentBudget.qftd_budget) <= 0
                }
                helperText={
                  currentBudget.qftd_budget !== "" &&
                  Number(currentBudget.qftd_budget) <= 0
                    ? "Must be a positive number"
                    : ""
                }
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                sx={{ width: "180px" }}
              />

              <FormControlLabel
                sx={{ ml: 2, whiteSpace: "nowrap" }} // apply flex end here to move it to the right
                control={
                  <Checkbox
                    checked={promotedOnly[brandId] || false}
                    onChange={() => handleTogglePromotedOnly(brandId)}
                    color="secondary"
                  />
                }
                label="Show promoted links only"
              />
            </Stack>
            {brandLinks?.map((link: any, index: number) => (
              <>
                <Stack
                  key={link.link_shared_uid + index}
                  flexDirection={"row"}
                  gap={4}
                  sx={{ mb: 2 }}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Stack gap={1} sx={{ width: "100%" }}>
                    <Stack gap={4} flexDirection={"row"}>
                      <Autocomplete
                        options={allLinks.filter((option: any) => {
                          // Find if this option is selected in another field (not the current one)
                          const isSelectedElsewhere = brandLinks.some(
                            (selected: any, i: number) =>
                              i !== index &&
                              selected.link_shared_uid ===
                                option.link_shared_uid
                          );

                          const isPromoted =
                            option.shown_in_active_table === true;
                          const showPromoted = promotedOnly[brandId] === true;

                          const matchesPromotionFilter = showPromoted
                            ? isPromoted
                            : !isPromoted;

                          return (
                            matchesPromotionFilter &&
                            (!isSelectedElsewhere ||
                              option.link_shared_uid === link.link_shared_uid)
                          );
                        })}
                        getOptionLabel={(option: any) =>
                          option?.link_name ? option.link_name : ""
                        }
                        fullWidth
                        value={link?.link_shared_uid ? link : null}
                        onChange={(_, newValue) => {
                          if (newValue)
                            handleLinkChange(brandId, index, newValue);
                          else handleLinkRemove(brandId, index);
                        }}
                        isOptionEqualToValue={(option, value) =>
                          option.link_shared_uid === value.link_shared_uid
                        }
                        disabled={totalAvailableLinks.length === 1}
                        disableClearable
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Link"
                            required
                            color="secondary"
                            helperText="This field is mandatory"
                            size="small"
                            placeholder={
                              !link?.link_shared_uid
                                ? "No link? Try toggling 'Show Promoted Links Only' to add more links"
                                : ""
                            }
                            inputProps={{
                              ...params.inputProps,
                              readOnly: true, // Make input non-editable
                            }}
                            slotProps={{
                              inputLabel: {
                                shrink: true,
                              },
                            }}
                          />
                        )}
                      />
                      {brandLinks.length > 1 && (
                        <IconButton
                          onClick={() => handleLinkRemove(brandId, index)}
                          sx={{
                            color: "#808080",
                            width: "40px",
                            height: "40px",
                          }}
                          size="small"
                        >
                          <CloseIcon />
                        </IconButton>
                      )}
                      {index === brandLinks.length - 1 && (
                        <Button
                          sx={{
                            whiteSpace: "nowrap",
                            height: "40px",
                            width: "177px",
                          }}
                          variant="outlined"
                          color="secondary"
                          endIcon={<AddIcon />}
                          onClick={() => handleAddLink(brandId, brand)}
                          disabled={availableLinks.length === 0}
                        >
                          Add another
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Stack>
              </>
            ))}
          </Box>
        );
      })}
    </Box>
  );
};

export default DuplicateBudgetEdit;
